import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { authService } from '../services/auth.service';

const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 프로필 정보
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    department: user?.department || '',
    role: user?.role || 'user',
  });

  // 비밀번호 변경
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        department: user.department || '',
        role: user.role || 'user',
      });
    }
  }, [user]);

  const handleProfileChange = (field: string, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user?.uid) return;

    setLoading(true);
    setMessage(null);

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        name: profile.name,
        department: profile.department,
      });

      setMessage({ type: 'success', text: '프로필이 저장되었습니다.' });
      setEditMode(false);
    } catch (error) {
      console.error('프로필 저장 실패:', error);
      setMessage({ type: 'error', text: '프로필 저장에 실패했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser || !user) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: '비밀번호는 최소 6자 이상이어야 합니다.' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // 재인증
      const credential = EmailAuthProvider.credential(
        user.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // 비밀번호 변경
      await updatePassword(currentUser, passwordData.newPassword);

      setMessage({ type: 'success', text: '비밀번호가 변경되었습니다.' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error: any) {
      console.error('비밀번호 변경 실패:', error);
      if (error.code === 'auth/wrong-password') {
        setMessage({ type: 'error', text: '현재 비밀번호가 올바르지 않습니다.' });
      } else {
        setMessage({ type: 'error', text: '비밀번호 변경에 실패했습니다.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleText = (role: string) => {
    return role === 'admin' ? '관리자' : '사용자';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        프로필 설정
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* 프로필 정보 카드 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', mr: 2 }}>
              <PersonIcon sx={{ fontSize: 48 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {profile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getRoleText(profile.role)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="이름"
              value={profile.name}
              onChange={(e) => handleProfileChange('name', e.target.value)}
              disabled={!editMode || loading}
              fullWidth
            />

            <TextField
              label="이메일"
              value={profile.email}
              disabled
              fullWidth
              helperText="이메일은 변경할 수 없습니다."
            />

            <TextField
              label="부서"
              value={profile.department}
              onChange={(e) => handleProfileChange('department', e.target.value)}
              disabled={!editMode || loading}
              fullWidth
            />

            <TextField
              label="권한"
              value={getRoleText(profile.role)}
              disabled
              fullWidth
              helperText="권한은 관리자만 변경할 수 있습니다."
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            {!editMode ? (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setEditMode(true)}
              >
                수정
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveProfile}
                  disabled={loading}
                >
                  저장
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    if (user) {
                      setProfile({
                        name: user.name || '',
                        email: user.email || '',
                        department: user.department || '',
                        role: user.role || 'user',
                      });
                    }
                  }}
                  disabled={loading}
                >
                  취소
                </Button>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 비밀번호 변경 카드 */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              비밀번호 변경
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="현재 비밀번호"
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              disabled={loading}
              fullWidth
            />

            <TextField
              label="새 비밀번호"
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              disabled={loading}
              fullWidth
              helperText="최소 6자 이상"
            />

            <TextField
              label="새 비밀번호 확인"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              disabled={loading}
              fullWidth
            />
          </Box>

          <Button
            variant="contained"
            startIcon={<LockIcon />}
            onClick={handlePasswordChange}
            disabled={loading || !passwordData.currentPassword || !passwordData.newPassword}
            sx={{ mt: 3 }}
          >
            비밀번호 변경
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserProfile;
